/* PLUSMAKER homepage product motion stages — lightweight CSS/SVG motion graphics
 * in the approved black-and-gold identity. Purely decorative (aria-hidden), no
 * numbers, no buy/sell instruction, no performance claims. Every stage renders a
 * fully-composed static frame when `animated` is false (prefers-reduced-motion). */

import { useRef } from "react";
import { useInView } from "framer-motion";

const GOLD = "#D4AF37";

/* Indicator — minimal gold candlestick system held inside soft, tilted orbital
 * rings with a warm bloom and drifting particles. Purely decorative: no grid
 * dashboard, no buy/sell interface, no numbers or performance claims. */
export function IndicatorStage({ animated = true }) {
  const an = (cls) => (animated ? cls : "");
  const dl = (s) => (animated ? { animationDelay: s } : undefined);
  const candles = [
    { x: 240, top: 196, h: 44, up: true },
    { x: 275, top: 178, h: 40, up: false },
    { x: 310, top: 150, h: 54, up: true },
    { x: 345, top: 128, h: 50, up: true },
    { x: 380, top: 104, h: 62, up: true },
  ];
  return (
    <div className="absolute inset-0 bg-[#0B0C10]" aria-hidden="true" data-testid="indicator-stage">
      <svg viewBox="0 0 600 400" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" focusable="false">
        <defs>
          <radialGradient id="ind-glow" cx="50%" cy="50%" r="50%">
            <stop stopColor="#D4AF37" stopOpacity="0.22" />
            <stop offset="0.5" stopColor="#D4AF37" stopOpacity="0.06" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* warm gold bloom behind the candles */}
        <ellipse cx="312" cy="182" rx="150" ry="120" fill="url(#ind-glow)" className={an("lp-core-glow")} />
        {/* orbital rings — tilted, Saturn-like */}
        <g transform="rotate(-16 305 195)">
          <ellipse cx="305" cy="195" rx="215" ry="74" fill="none" stroke="rgba(212,175,55,0.14)" strokeWidth="1" strokeDasharray="4 14" className={an("lp-orbit")} />
          <ellipse cx="305" cy="195" rx="156" ry="54" fill="none" stroke="rgba(212,175,55,0.30)" strokeWidth="1" />
          <g className={an("lp-core-glow")}>
            <circle cx="461" cy="195" r="9" fill="#D4AF37" fillOpacity="0.18" />
            <circle cx="461" cy="195" r="3.4" fill="#F3D77A" />
          </g>
          <circle cx="149" cy="195" r="2.2" fill={GOLD} opacity="0.5" />
        </g>
        {/* candlesticks — abstract, label-free, gently ascending */}
        {candles.map((c, i) => (
          <g key={c.x} className={an("lp-candle")} style={dl(`${i * 0.7}s`)}>
            <line x1={c.x} y1={c.top - 14} x2={c.x} y2={c.top + c.h + 14} stroke={c.up ? GOLD : "#94A3B8"} strokeWidth="1.1" opacity="0.65" />
            <rect x={c.x - 8} y={c.top} width="16" height={c.h} rx="2"
              fill={c.up ? "rgba(212,175,55,0.85)" : "rgba(148,163,184,0.32)"}
              stroke={c.up ? "#F0D98A" : "#8FA0B4"} strokeWidth="1" />
          </g>
        ))}
        {/* drifting particles */}
        {[[150, 110], [500, 130], [120, 300], [478, 322], [300, 58], [92, 210]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 ? 1.8 : 1.2} fill={GOLD} className={an("lp-particle")} style={dl(`${i * 1.5}s`)} />
        ))}
      </svg>
    </div>
  );
}

/* Automated Trading — a sparse geometric system core with four process nodes
 * (Analyze / Optimize / Execute / Monitor) on concentric orbital rings. No
 * infinity symbol, no EA logo, no metrics, signals or performance claims. */
export function AutomationStage({ animated = true }) {
  const an = (cls) => (animated ? cls : "");
  const dl = (s) => (animated ? { animationDelay: s } : undefined);
  const cx = 300, cy = 240;
  const outerHex = "380,240 340,309.3 260,309.3 220,240 260,170.7 340,170.7";
  const innerHex = "358,240 329,290.2 271,290.2 242,240 271,189.8 329,189.8";
  const nodes = [
    { label: "Analyze", x: 300, y: 90, lx: 300, ly: 66, anchor: "middle" },
    { label: "Optimize", x: 450, y: 240, lx: 472, ly: 244, anchor: "start" },
    { label: "Execute", x: 300, y: 390, lx: 300, ly: 416, anchor: "middle" },
    { label: "Monitor", x: 150, y: 240, lx: 128, ly: 244, anchor: "end" },
  ];
  return (
    <div className="absolute inset-0 bg-[#0B0C10]" aria-hidden="true" data-testid="automation-stage">
      <svg viewBox="0 0 600 480" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" focusable="false">
        <defs>
          <radialGradient id="auto-glow" cx="50%" cy="50%" r="50%">
            <stop stopColor="#D4AF37" stopOpacity="0.26" />
            <stop offset="0.55" stopColor="#D4AF37" stopOpacity="0.05" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* large faint orbital ring + fine dotted ring */}
        <circle cx={cx} cy={cy} r="195" fill="none" stroke="rgba(212,175,55,0.10)" strokeWidth="1" />
        <circle cx={cx} cy={cy} r="150" fill="none" stroke="rgba(212,175,55,0.32)" strokeWidth="1" strokeDasharray="1 7" strokeLinecap="round" className={an("lp-orbit")} />
        {/* core → node connectors (thin, solid) */}
        {nodes.map((n) => (
          <line key={n.label} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke={GOLD} strokeWidth="1" opacity="0.4" />
        ))}
        {/* warm central bloom */}
        <ellipse cx={cx} cy={cy} rx="112" ry="112" fill="url(#auto-glow)" className={an("lp-core-glow")} />
        {/* layered hexagonal geometric core — no square, subtle central gold point */}
        <g className={an("lp-core-glow")}>
          <polygon points={outerHex} fill="none" stroke={GOLD} strokeWidth="1.4" />
          <polygon points={innerHex} fill="rgba(212,175,55,0.04)" stroke="rgba(212,175,55,0.6)" strokeWidth="1" />
        </g>
        <circle cx={cx} cy={cy} r="13" fill="#D4AF37" fillOpacity="0.18" className={an("lp-core-glow")} />
        <circle cx={cx} cy={cy} r="6.5" fill="#F7DE8A" />
        {/* process nodes with external labels */}
        {nodes.map((n, i) => (
          <g key={n.label}>
            <g className={an("lp-flow-node")} style={dl(`${i * 1.4}s`)}>
              <circle cx={n.x} cy={n.y} r="14" fill="#D4AF37" fillOpacity="0.16" />
              <circle cx={n.x} cy={n.y} r="8" fill="#F3D77A" stroke={GOLD} strokeWidth="1" />
            </g>
            <text x={n.lx} y={n.ly} textAnchor={n.anchor} fill="#D9B658" fontSize="13" fontFamily="'JetBrains Mono', monospace" letterSpacing="1.5">{n.label}</text>
          </g>
        ))}
        {/* drifting particles */}
        {[[210, 150], [430, 180], [160, 330], [470, 330], [300, 48], [120, 220], [500, 260]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 ? 1.7 : 1.1} fill={GOLD} className={an("lp-particle")} style={dl(`${i * 1.5}s`)} />
        ))}
      </svg>
    </div>
  );
}

/* Member Tools — ONE unified, lightweight-animated composition: a candlestick
 * cluster inside a tilted orbital ellipse (Indicator) whose energy flows along a
 * gold connector into a hexagonal automation core with rotating orbital rings
 * (Automated Trading). Flat, black + gold + muted blue-grey. No 3D, no cards, no
 * infinity, no EA logo, no centre box, no text. `animated=false` (reduced motion)
 * renders a fully composed static frame (no SMIL). */
export function MemberToolsStage({ animated = true }) {
  const A = animated;
  const COOL = "#BFD6EE";
  const trails = [
    { d: "M500 210 C 560 226 575 210 600 230 C 632 250 648 232 745 250", stroke: "rgba(212,175,55,0.55)" },
    { d: "M500 252 C 560 236 575 252 600 230 C 632 208 648 226 745 210", stroke: "rgba(191,214,238,0.4)" },
    { d: "M508 230 C 565 230 582 230 600 230 C 642 230 662 230 742 230", stroke: "rgba(212,175,55,0.3)" },
  ];
  const candles = [
    { x: 206, top: 300, h: 66, k: "blue" }, { x: 236, top: 282, h: 58, k: "gold" },
    { x: 266, top: 296, h: 50, k: "grey" }, { x: 296, top: 260, h: 78, k: "gold" },
    { x: 326, top: 240, h: 64, k: "blue" }, { x: 356, top: 212, h: 82, k: "gold" },
    { x: 386, top: 188, h: 92, k: "gold" }, { x: 416, top: 216, h: 56, k: "grey" },
  ];
  const cfill = { gold: "rgba(212,175,55,0.9)", blue: "rgba(91,122,153,0.45)", grey: "rgba(148,163,184,0.32)" };
  const cstroke = { gold: "#F0D98A", blue: "#7FA8CC", grey: "#8FA0B4" };
  const engineNodes = [
    { x: 900, y: 110, icon: <><rect x="-6" y="0" width="4" height="10" fill={GOLD} /><rect x="0" y="-5" width="4" height="15" fill={GOLD} /><rect x="6" y="-2" width="4" height="12" fill={GOLD} /></> },
    { x: 1020, y: 230, icon: <><ellipse cx="0" cy="-5" rx="8" ry="3" fill="none" stroke={GOLD} strokeWidth="1.4" /><path d="M-8 -5 V5 A8 3 0 0 0 8 5 V-5" fill="none" stroke={GOLD} strokeWidth="1.4" /><line x1="-8" y1="0" x2="8" y2="0" stroke={GOLD} strokeWidth="1.2" /></> },
    { x: 900, y: 350, icon: <><circle r="6" fill="none" stroke={GOLD} strokeWidth="1.4" />{[0, 45, 90, 135].map((a) => <line key={a} x1="0" y1="-9" x2="0" y2="9" stroke={GOLD} strokeWidth="1.4" transform={`rotate(${a})`} />)}<circle r="2.4" fill={GOLD} /></> },
    { x: 780, y: 230, icon: <><line x1="-8" y1="-4" x2="8" y2="-4" stroke={GOLD} strokeWidth="1.4" /><circle cx="3" cy="-4" r="2.4" fill={GOLD} /><line x1="-8" y1="4" x2="8" y2="4" stroke={GOLD} strokeWidth="1.4" /><circle cx="-3" cy="4" r="2.4" fill={GOLD} /></> },
  ];
  return (
    <div className="absolute inset-0 bg-[#0B0C10]" aria-hidden="true" data-testid="member-tools-stage" data-animated={A}>
      <svg viewBox="0 0 1200 500" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" focusable="false">
        <defs>
          <radialGradient id="mt-core-glow" cx="50%" cy="50%" r="50%">
            <stop stopColor="#FCE9A6" stopOpacity="0.9" /><stop offset="0.4" stopColor="#D4AF37" stopOpacity="0.32" /><stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="mt-ind-glow" cx="50%" cy="50%" r="50%">
            <stop stopColor="#D4AF37" stopOpacity="0.14" /><stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ==================== LEFT — INDICATOR ==================== */}
        <ellipse cx="300" cy="230" rx="150" ry="104" fill="url(#mt-ind-glow)" className={A ? "gpu-breathe" : undefined} />
        {/* subtle market-structure grid */}
        <g stroke="rgba(212,175,55,0.06)" strokeWidth="1">
          {[180, 230, 280, 330, 380, 430].map((x) => <line key={x} x1={x} y1="120" x2={x} y2="360" />)}
          {[170, 230, 290, 350].map((y) => <line key={y} x1="120" y1={y} x2="480" y2={y} />)}
        </g>
        {/* orbital lines — gold + cool-white, slow rotation */}
        <ellipse cx="300" cy="230" rx="235" ry="165" fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="1" strokeDasharray="2 10" transform="rotate(-6 300 230)" />
        <g className={A ? "gpu-spin" : undefined} style={A ? { transformOrigin: "300px 230px", animationDuration: "90s" } : undefined}>
          <ellipse cx="300" cy="230" rx="210" ry="150" fill="none" stroke="rgba(212,175,55,0.3)" strokeWidth="1" />
        </g>
        <g className={A ? "gpu-spin-rev" : undefined} style={A ? { transformOrigin: "300px 230px", animationDuration: "70s" } : undefined}>
          <ellipse cx="300" cy="230" rx="166" ry="116" fill="none" stroke="rgba(191,214,238,0.22)" strokeWidth="1" strokeDasharray="4 9" />
        </g>
        {/* candlesticks */}
        <g>
          {candles.map((c, ci) => (
            <g key={c.x} className={A ? "gpu-bob" : undefined} style={A ? { animationDuration: `${5 + (ci % 3)}s`, animationDelay: `${ci * 0.4}s` } : undefined}>
              <line x1={c.x} y1={c.top - 14} x2={c.x} y2={c.top + c.h + 14} stroke={cstroke[c.k]} strokeWidth="1.3" opacity="0.6" />
              <rect x={c.x - 10} y={c.top} width="20" height={c.h} rx="2.5" fill={cfill[c.k]} stroke={cstroke[c.k]} strokeWidth="1.1" />
            </g>
          ))}
        </g>
        {/* smooth analysis curve */}
        <path d="M150 335 C 240 320 250 292 300 292 S 380 236 470 214" fill="none" stroke={GOLD} strokeWidth="1.6" strokeLinecap="round" opacity="0.85" strokeDasharray="10 10" className={A ? "gpu-dash" : undefined} style={A ? { animationDuration: "5s" } : undefined} />
        {/* accent orbit dots */}
        <circle cx="110" cy="200" r="4" fill="#F3D77A" opacity="0.8" />
        <circle cx="560" cy="270" r="3" fill={COOL} opacity="0.7" />

        {/* ==================== CENTER — DATA BRIDGE ==================== */}
        {/* flowing data trails */}
        {trails.map((t, i) => (
          <path key={i} d={t.d} fill="none" stroke={t.stroke} strokeWidth="1.4" strokeLinecap="round" strokeDasharray="12 9" className={A ? "gpu-dash" : undefined} style={A ? { animationDuration: `${2.6 + i * 0.6}s` } : undefined} />
        ))}
        {/* travelling particles + energy pulses */}
        {A && [0, 1, 2, 3].map((n) => (
          <circle key={n} cx="505" cy={230 + (n % 2 ? 7 : -7)} r={n % 2 ? 2 : 3} fill={n % 2 ? COOL : "#FCE9A6"}
            className="gpu-travel" style={{ "--gpu-travel": "240px", animationDelay: `${n * 0.8}s` }} />
        ))}
        {/* central connection core + subtle pulse */}
        <circle cx="600" cy="230" r="42" fill="url(#mt-core-glow)" className={A ? "gpu-breathe" : undefined} />
        {A && <circle cx="600" cy="230" r="18" fill="none" stroke="rgba(212,175,55,0.5)" strokeWidth="1.2" className="gpu-pulse-ring" />}
        <circle cx="600" cy="230" r="18" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1" />
        <circle cx="600" cy="230" r="7.5" fill="#FCE9A6" />
        {[[-30, 0], [30, 0], [0, -30], [0, 30]].map(([dx, dy], i) => (
          <line key={i} x1={600 + dx * 0.55} y1={230 + dy * 0.55} x2={600 + dx} y2={230 + dy} stroke="rgba(212,175,55,0.5)" strokeWidth="1" />
        ))}

        {/* ==================== RIGHT — EXPERT ADVISORS ==================== */}
        <circle cx="900" cy="230" r="150" fill="url(#mt-ind-glow)" className={A ? "gpu-breathe" : undefined} />
        <g className={A ? "gpu-spin" : undefined} style={A ? { transformOrigin: "900px 230px", animationDuration: "80s" } : undefined}>
          <circle cx="900" cy="230" r="150" fill="none" stroke="rgba(212,175,55,0.12)" strokeWidth="1" strokeDasharray="1 8" />
          <circle cx="900" cy="230" r="120" fill="none" stroke="rgba(191,214,238,0.16)" strokeWidth="1" />
        </g>
        <g className={A ? "gpu-spin-rev" : undefined} style={A ? { transformOrigin: "900px 230px", animationDuration: "60s" } : undefined}>
          <circle cx="900" cy="230" r="92" fill="none" stroke="rgba(212,175,55,0.2)" strokeWidth="1" strokeDasharray="2 7" />
        </g>
        {/* circuit pathways core → radial nodes */}
        {engineNodes.map((n, i) => (
          <line key={i} x1="900" y1="230" x2={n.x} y2={n.y} stroke="rgba(212,175,55,0.35)" strokeWidth="1" strokeDasharray="4 6" className={A ? "gpu-dash" : undefined} style={A ? { animationDuration: "4s" } : undefined} />
        ))}
        {/* short circuit pins on the processor */}
        {[-1, 0, 1].map((k) => (
          <g key={k} stroke="rgba(212,175,55,0.6)" strokeWidth="1.4">
            <line x1={868 - 12} y1={230 + k * 16} x2="868" y2={230 + k * 16} />
            <line x1="932" y1={230 + k * 16} x2={932 + 12} y2={230 + k * 16} />
            <line x1={900 + k * 16} y1={198 - 12} x2={900 + k * 16} y2="198" />
            <line x1={900 + k * 16} y1="262" x2={900 + k * 16} y2={262 + 12} />
          </g>
        ))}
        {/* processor core + gear */}
        <rect x="868" y="198" width="64" height="64" rx="9" fill="rgba(212,175,55,0.06)" stroke={GOLD} strokeWidth="1.5" className={A ? "gpu-breathe" : undefined} />
        <g transform="translate(900 230)">
          <circle r="12" fill="none" stroke={GOLD} strokeWidth="1.5" />
          {[0, 45, 90, 135].map((a) => <line key={a} x1="0" y1="-16" x2="0" y2="16" stroke={GOLD} strokeWidth="1.5" transform={`rotate(${a})`} />)}
          <circle r="4.5" fill="#FCE9A6" />
        </g>
        {/* radial system nodes */}
        {engineNodes.map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r="18" fill="#0B0C10" stroke="rgba(212,175,55,0.55)" strokeWidth="1.2" />
            <g transform={`translate(${n.x} ${n.y})`}>{n.icon}</g>
          </g>
        ))}
        {/* orbiting dot on the engine ring */}
        {A && (
          <g className="gpu-spin" style={{ transformOrigin: "900px 230px", animationDuration: "16s" }}>
            <circle cx="900" cy="110" r="2.6" fill={COOL} />
          </g>
        )}

        {/* scattered particles */}
        {[[150, 150], [1060, 150], [140, 340], [1070, 330], [520, 120], [700, 380], [1000, 100]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 ? 1.8 : 1.1} fill={i % 3 === 0 ? COOL : GOLD} opacity="0.5"
            className={A ? "gpu-drift" : undefined} style={A ? { animationDuration: `${4 + i}s`, animationDelay: `${i * 0.5}s` } : undefined} />
        ))}

        {/* labels */}
        <g fontFamily="'JetBrains Mono', monospace" fill={GOLD} fontSize="19" letterSpacing="6" textAnchor="middle">
          <line x1="180" y1="447" x2="235" y2="447" stroke={GOLD} strokeOpacity="0.5" strokeWidth="1" />
          <text x="300" y="453">INDICATOR</text>
          <line x1="365" y1="447" x2="420" y2="447" stroke={GOLD} strokeOpacity="0.5" strokeWidth="1" />
          <line x1="740" y1="447" x2="795" y2="447" stroke={GOLD} strokeOpacity="0.5" strokeWidth="1" />
          <text x="900" y="453">EXPERT ADVISORS</text>
          <line x1="1005" y1="447" x2="1060" y2="447" stroke={GOLD} strokeOpacity="0.5" strokeWidth="1" />
        </g>
      </svg>
    </div>
  );
}

/* Counter-rotation keeps each label upright as its node follows the ellipse.
 * Fixed phase offsets preserve spacing, including in the static fallback. */
function LearnOrbitNode({ label, phase }) {
  return (
    <g transform={`rotate(${phase})`} data-testid={`ll-orbit-node-${label.toLowerCase()}`}>
      <g className="ll-home__orbit-travel">
        <g transform="translate(205 0)">
          <g className="ll-home__orbit-counter">
            <g transform={`rotate(${-phase}) scale(1 ${1 / 0.72}) rotate(24)`}>
              <circle r="11" fill="#D4AF37" fillOpacity="0.04" stroke="#D4AF37" strokeOpacity="0.28" strokeWidth="0.8" />
              <circle r="3.5" fill="#EBD18B" />
              <text y="30" textAnchor="middle" className="ll-home__node-label">{label}</text>
            </g>
          </g>
        </g>
      </g>
    </g>
  );
}

/* Homepage-only abstract knowledge core. CSS handles responsive detail and a
 * fully composed reduced-motion frame; no content, access or purchase logic. */
export function LearnHubStage({ animated = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "100px" });
  return (
    <div ref={ref} className="ll-home__stage" aria-hidden="true" data-testid="learn-hub-stage"
      data-animated={animated} style={{ "--ll-play-state": inView ? "running" : "paused" }}>
      <svg viewBox="0 0 600 520" fill="none" preserveAspectRatio="xMidYMid meet" focusable="false">
        <defs>
          <radialGradient id="ll-home-halo">
            <stop stopColor="#D4AF37" stopOpacity="0.2" />
            <stop offset="0.38" stopColor="#BE9440" stopOpacity="0.08" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="ll-home-core" cx="30%" cy="24%" r="78%">
            <stop stopColor="#FFF1C5" />
            <stop offset="0.22" stopColor="#E3C16C" />
            <stop offset="0.52" stopColor="#A17C32" />
            <stop offset="0.82" stopColor="#483416" />
            <stop offset="1" stopColor="#151109" />
          </radialGradient>
          <linearGradient id="ll-home-rim" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#F6E3A8" stopOpacity="0.85" />
            <stop offset="0.5" stopColor="#D4AF37" stopOpacity="0.2" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0.06" />
          </linearGradient>
          <linearGradient id="ll-home-ring" x1="0" y1="0" x2="1" y2="1">
            <stop stopColor="#D4AF37" stopOpacity="0.08" />
            <stop offset="0.45" stopColor="#D4AF37" stopOpacity="0.45" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0.12" />
          </linearGradient>
        </defs>
        <circle cx="300" cy="260" r="244" fill="url(#ll-home-halo)" className="ll-home__halo" />
        <g transform="translate(300 260) rotate(32) scale(1 0.64)">
          <circle r="170" stroke="#D4AF37" strokeOpacity="0.17" strokeWidth="1" />
        </g>
        <g transform="translate(300 260) rotate(-24) scale(1 0.72)">
          <circle r="205" stroke="url(#ll-home-ring)" strokeWidth="1" />
          {["Learn", "Watch", "Attend", "Explore"].map((label, i) => (
            <LearnOrbitNode key={label} label={label} phase={225 + i * 90} />
          ))}
        </g>
        <g data-testid="ll-knowledge-core">
          <circle cx="300" cy="260" r="72" stroke="url(#ll-home-rim)" strokeOpacity="0.35" strokeWidth="0.7" />
          <circle cx="300" cy="260" r="56" fill="url(#ll-home-core)" stroke="url(#ll-home-rim)" strokeWidth="1.2" />
          <ellipse cx="300" cy="260" rx="23" ry="55" transform="rotate(-32 300 260)" stroke="#FFEBB0" strokeOpacity="0.22" strokeWidth="0.7" />
          <ellipse cx="300" cy="260" rx="55" ry="19" transform="rotate(-32 300 260)" stroke="#FFEBB0" strokeOpacity="0.16" strokeWidth="0.7" />
          <path d="M251 244 A52 52 0 0 1 313 209" stroke="#FFF0C2" strokeOpacity="0.6" strokeWidth="1.2" strokeLinecap="round" />
        </g>
        <g fill="#DFC278" className="ll-home__dust">
          {[[140, 100], [421, 117], [456, 354], [198, 414], [92, 324]].map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={i % 2 ? 1.6 : 1} opacity="0.4"
              className="ll-home__particle" style={{ animationDelay: `${i * -3}s` }} />
          ))}
        </g>
      </svg>
    </div>
  );
}
